import { useCallback } from "react";
import { useCmsContext } from "@rotorstudios/fe-cms";
import { useQuery } from "@tanstack/react-query";

import type { iCmeSiloUi } from "./types";
import { CMS_DATA_SOURCES, CMS_DATASOURCES_CME_OPTIONS_UI_SILO } from "./static";
import { useFetchCMSData } from "./useFetchCMSData";

const CACHE_TIME = 3 * 60 * 1000; // 3 minutes in ms

const useCmeSiloUiData = () => {
  const { fetchData } = useFetchCMSData();
  const { siloManifest } = useCmsContext();

  const path = siloManifest?.[CMS_DATA_SOURCES]?.get(CMS_DATASOURCES_CME_OPTIONS_UI_SILO);

  // *************************
  // CME Options Ui Silo Data
  // *************************
  const getCmeOptionsUiSiloData = async (): Promise<iCmeSiloUi> => {
    if (!path)
      throw new Error("CME Options UI Silo data source not found in silo manifest");
    const raw = await fetchData(path);
    return raw?.data;
  };

  const { data: cmeSiloUiData } = useQuery({
    queryKey: ["cmeSiloUiData", path],
    queryFn: () => getCmeOptionsUiSiloData(),
    enabled: Boolean(path),
    staleTime: CACHE_TIME,
  });

  // *******************
  // Methods
  // *******************
  const getCmeSiloUiOptions = useCallback(
    (name: string, feature: string) =>
      cmeSiloUiData?.features[feature]?.options[name] || {},
    [cmeSiloUiData?.features],
  );

  return { cmeSiloUiData, getCmeSiloUiOptions };
};

export default useCmeSiloUiData;
